import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { CheckCircle2, ScanFace } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import FaceCapture from "@/components/FaceCapture";

// Employee self-service: enroll your own face once (re-enrollment is done by HR).
export default function FaceEnroll() {
    const { user } = useAuth();
    const empId = user?.employee_id;
    const [status, setStatus] = useState(null);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (empId) api.get(`/employees/${empId}/face/status`).then(({ data }) => setStatus(data)).catch(() => setStatus({ enrolled: false }));
    }, [empId]);

    const done = async (descriptors) => {
        setSaving(true);
        try {
            await api.post(`/employees/${empId}/face/enroll`, { face_descriptors: descriptors });
            toast.success("Face enrolled — you can now clock in with Face ID");
            setStatus({ enrolled: true });
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) || "Enrollment failed, please try again");
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="p-6 sm:p-10 max-w-2xl" data-testid="face-enroll-page">
            <div className="overline flex items-center gap-1.5"><ScanFace className="w-3.5 h-3.5" /> Face ID</div>
            <h1 className="mt-2 font-display font-extrabold text-4xl sm:text-5xl text-ink">Enroll your face</h1>
            <p className="mt-3 text-sm text-ink/60">Used only to confirm it&apos;s really you when you clock in. Matching happens on your device.</p>
            <div className="mt-8">
                {!empId && <div className="text-sm text-ink/60" data-testid="face-enroll-no-employee">Your account isn&apos;t linked to an employee record yet. Ask HR to link it.</div>}
                {empId && status?.enrolled && (
                    <div className="bg-emerald-50 border border-emerald-200 p-6 flex items-start gap-3" data-testid="face-enroll-done">
                        <CheckCircle2 className="w-6 h-6 text-emerald-600 shrink-0" />
                        <div>
                            <div className="font-semibold text-ink">You&apos;re all set</div>
                            <p className="text-sm text-ink/60">Your face is enrolled. To change it, ask HR to re-enroll you.</p>
                            <Link to="/check-in" className="mt-3 inline-block text-sm text-klein hover:underline" data-testid="face-enroll-go-checkin">Go to check-in →</Link>
                        </div>
                    </div>
                )}
                {empId && status && !status.enrolled && (saving
                    ? <div className="text-sm text-ink/60">Saving…</div>
                    : <FaceCapture mode="enroll" requiredSamples={3} onComplete={done} />)}
            </div>
        </div>
    );
}
